import {type ChangeEvent, useCallback, useEffect, useState} from "react";
import {Link, useParams} from "react-router-dom";
import {
    Box,
    Breadcrumbs,
    Button,
    CircularProgress,
    Container,
    List,
    Pagination,
    Paper,
    Typography,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {useTranslation} from "react-i18next";

import {useAlert} from "../context/AlertContext";
import {ChapterDetailsResponse} from "../types";
import {getMyChapterDetails} from "../api/api";
import BreadcrumbLink from "../components/BreadcrumbLink";

const MyChapterDetailsPage = () => {
    const {t} = useTranslation();

    const {chapterId} = useParams<{ chapterId?: string }>();
    const safeChapterId = chapterId ?? "";
    const {showAlert} = useAlert();

    const [data, setData] = useState<ChapterDetailsResponse | null>(null);
    const [page, setPage] = useState(1);

    const [loading, setLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    const pageSize = 10;

    const fetchData = useCallback(async (uiPage: number) => {
        if (!safeChapterId) return;

        setLoading(true);
        setHasError(false);

        try {
            const res = await getMyChapterDetails(Number(safeChapterId), uiPage - 1, pageSize);
            setData(res);
            setPage((res.lessons?.page?.number ?? 0) + 1);
        } catch (err: any) {
            setHasError(true);
            setData(null);
            showAlert(err?.message ?? t("errors.contactAdmins"), "error");
        } finally {
            setLoading(false);
        }
    }, [safeChapterId, showAlert, t]);

    useEffect(() => {
        fetchData(1);
    }, [fetchData]);

    const handlePageChange = (_: ChangeEvent<unknown>, value: number) => {
        fetchData(value);
    };

    if (loading && !data) {
        return (
            <Container sx={{mt: 6, display: "flex", justifyContent: "center"}}>
                <CircularProgress/>
            </Container>
        );
    }

    if (hasError) {
        return (
            <Container sx={{mt: 6, display: "flex", flexDirection: "column", alignItems: "center", gap: 2}}>
                <Typography color="text.secondary">
                    {t("mychapterdetails.errors.loadFailed")}
                </Typography>

                <Box sx={{display: "flex", gap: 2}}>
                    <Button variant="contained" onClick={() => fetchData(page || 1)}>
                        {t("common.retry")}
                    </Button>

                    <Button component={Link} to="/my-courses" variant="outlined">
                        {t("common.backToMyCourses")}
                    </Button>
                </Box>
            </Container>
        );
    }

    if (!data) return null;

    const lessons = data.lessons?.content ?? [];
    const totalPages = data.lessons?.page?.totalPages ?? 1;

    return (
        <Box sx={{px: {xs: 3, md: 3}}}>
            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small"/>}
                sx={{mb: 2}}
            >
                <BreadcrumbLink to="/my-courses">
                    {t("breadcrumb.myCourses")}
                </BreadcrumbLink>

                <BreadcrumbLink to={`/course-details/${data.courseId}`}>
                    {data.courseTitle || t("breadcrumb.courseDetails")}
                </BreadcrumbLink>

                <Typography color="text.primary" variant="body2">
                    <strong>{data.title || t("breadcrumb.chapterDetails")}</strong>
                </Typography>
            </Breadcrumbs>

            <Box sx={{mb: 2}}>
                <Typography sx={{fontSize: 28, fontWeight: 800}} noWrap>
                    {data.title}
                </Typography>
            </Box>

            <List sx={{display: "flex", flexDirection: "column", gap: 3}}>
                {lessons.map((lesson) => (
                    <Paper
                        key={lesson.id}
                        component={Link}
                        to={`/lesson-details/${lesson.id}`}
                        elevation={0}
                        sx={(theme) => ({
                            px: 3,
                            py: 2,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                            textDecoration: "none",
                            color: "inherit",
                            cursor: "pointer",
                            transition: "transform 160ms ease, box-shadow 160ms ease, background-color 160ms ease",
                            "&:hover": {
                                transform: "translateY(-3px)",
                                boxShadow:
                                    theme.palette.mode === "dark"
                                        ? "0 10px 24px rgba(0,0,0,0.35)"
                                        : "0 10px 24px rgba(0,0,0,0.08)",
                                backgroundColor: theme.palette.action.hover,
                            },
                            "&:hover .lesson-arrow": {
                                transform: "translateX(4px)",
                            },
                            "&:focus-visible": {
                                outline: `2px solid ${theme.palette.primary.main}`,
                                outlineOffset: 2,
                            },
                        })}
                    >
                        <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2}}>
                            <Typography sx={{fontWeight: 700}} noWrap>
                                {lesson.title}
                            </Typography>

                            <ArrowForwardIosIcon
                                className="lesson-arrow"
                                sx={{
                                    fontSize: 16,
                                    color: "text.secondary",
                                    transition: "transform 160ms ease",
                                }}
                            />
                        </Box>
                    </Paper>
                ))}
            </List>

            {lessons.length === 0 && (
                <Typography sx={{mt: 3}} color="text.secondary">
                    {t("mychapterdetails.noLessons")}
                </Typography>
            )}

            <Box sx={{display: "flex", justifyContent: "center", mt: 3}}>
                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                />
            </Box>
        </Box>
    );
};

export default MyChapterDetailsPage;